// ------------------------------------------------------------------
// Busca e vínculo de clientes na IUGU.
//
// Centraliza a checagem "este sócio já tem conta na IUGU?" usada tanto
// pela ação em massa (ModalBulkActions) quanto pelo perfil do sócio
// (UserShow). Antes cada tela fazia a sua, e a da ação em massa lia o
// retorno da API como objeto quando ele é uma lista — o que fazia a
// verificação falhar sempre e criar contas duplicadas.
// ------------------------------------------------------------------

export type IuguCustomer = {
  id: string;
  email?: string;
  name?: string;
  cpf_cnpj?: string;
  [key: string]: any;
};

export type IuguMatch = {
  /** Cliente com identidade confirmada (e-mail ou documento idêntico). */
  customer: IuguCustomer | null;
  /** Como a identidade foi confirmada. */
  matchedBy: "email" | "documento" | null;
  /** Tudo que a busca trouxe, para escolha manual quando não há certeza. */
  candidates: IuguCustomer[];
};

export const onlyDigits = (value?: string | null) =>
  (value || "").replace(/\D/g, "");

/**
 * Falha ao falar com a IUGU, já com a mensagem para o operador.
 * `refused` = a IUGU recusou os dados (nada foi criado); senão é falha de
 * comunicação e não dá para garantir o que aconteceu do lado de lá.
 */
export class IuguError extends Error {
  refused: boolean;

  constructor(message: string, refused = false) {
    super(message);
    this.name = "IuguError";
    this.refused = refused;
    // Compilando para ES5, subclasse de Error perde o protótipo
    Object.setPrototypeOf(this, IuguError.prototype);
  }
}

/** Mensagem legível de qualquer erro vindo das funções deste arquivo. */
export const iuguErrorMessage = (error: any) =>
  error?.name === "IuguError" && error.message
    ? error.message
    : "falha de comunicação com a IUGU";

const FIELD_LABELS: Record<string, string> = {
  email: "E-mail",
  cpf_cnpj: "CPF/CNPJ",
  name: "Nome",
};

/**
 * Traduz o `errors` que a IUGU devolve ao recusar um cadastro.
 * Formatos vistos: { campo: ["mensagem"] }, "texto" ou ["texto"]. As
 * mensagens vêm misturadas em inglês e português ("is invalid",
 * "não é válido").
 */
export const describeIuguErrors = (errors: any): string => {
  if (!errors) return "";
  if (typeof errors === "string") return errors;
  if (Array.isArray(errors)) return errors.join("; ");
  if (typeof errors === "object") {
    return Object.entries(errors)
      .map(([field, messages]) => {
        const label = FIELD_LABELS[field] || field;
        const text = ([] as any[]).concat(messages).join(", ");
        if (/invalid|inv[áa]lid|n[ãa]o [ée] v[áa]lid/i.test(text)) {
          return `${label} inválido`;
        }
        if (/blank|branco|vazi|obrigat/i.test(text)) {
          return `${label} em branco`;
        }
        return `${label}: ${text}`;
      })
      .join("; ");
  }
  return String(errors);
};

/** Lê o corpo como JSON; corpo vazio ou inválido vira null. */
const readJson = async (response: Response) => {
  try {
    return await response.json();
  } catch (error) {
    return null;
  }
};

const authHeaders = () => {
  const auth = localStorage.getItem("auth");
  const authJson = auth ? JSON.parse(auth) : auth;
  return {
    "Content-Type": "application/json",
    Authorization: "Bearer " + authJson?.access_token,
  };
};

// ------------------------------------------------------------------
// Ritmo das chamadas. A IUGU limita requisições: responde HTTP 429
// { "error": "Too many requests", "code": "0116" }, que a biblioteca do
// backend transforma em HTTP 500 "Unknown Error". Nos logs de produção de
// 15/10/2026, cada rodada da ação em massa passava ~100 chamadas e daí em
// diante TODAS eram barradas por 1-2 minutos — o laço seguia disparando
// sem pausa (até ~8 por segundo) e mantinha o bloqueio. A IUGU só publica
// o limite da conta de teste (50/min); o de produção inferido é ~100/min.
// ------------------------------------------------------------------
const IUGU_MIN_INTERVAL_MS = 800; // ~75 chamadas por minuto, com folga
// Só a CONSULTA é repetida (é só leitura); criar conta nunca é repetido.
// Na ficha do sócio a espera é curta (alguém aguardando na tela); a ação em
// massa usa esperas longas, para a janela de limite da IUGU zerar.
export const SEARCH_RETRY_SHORT_MS = [2000, 5000];
export const SEARCH_RETRY_LONG_MS = [10000, 30000, 60000];
let nextIuguSlot = 0;

const sleep = (ms: number) =>
  new Promise((resolve) => setTimeout(resolve, ms));

export type IuguRetryOptions = {
  /** Esperas antes de cada nova tentativa da consulta. */
  retryDelaysMs?: number[];
  /** Avisado antes de cada espera (para a tela mostrar o que acontece). */
  onRetryWait?: (delayMs: number) => void;
  /** Quando devolve true, a espera é cortada e a consulta desiste. */
  shouldStop?: () => boolean;
};

/** Espera que pode ser cortada (ex.: botão "Interromper"). */
const waitOrStop = async (ms: number, shouldStop?: () => boolean) => {
  const end = Date.now() + ms;
  while (Date.now() < end) {
    if (shouldStop?.()) throw new IuguError("operação interrompida");
    await sleep(Math.min(500, end - Date.now()));
  }
};

/** Espaça as chamadas à IUGU desta aba (no máximo ~75 por minuto). */
const waitIuguSlot = async () => {
  const now = Date.now();
  const slot = Math.max(now, nextIuguSlot);
  nextIuguSlot = slot + IUGU_MIN_INTERVAL_MS;
  if (slot > now) await sleep(slot - now);
};

/**
 * E-mail que a IUGU não consegue nem pesquisar: campo com mais de um
 * endereço ("a@x.com, b@y.com", "a@x.com / b@y.com"), espaço no meio, sem
 * @ ou com dois. A busca volta "Parâmetros de busca inválidos" e o
 * cadastro seria recusado — então, nesses casos, busca só pelo documento.
 * Só reconhece; o campo da ficha nunca é alterado.
 */
export const isSingleEmail = (value?: string | null) =>
  /^[^\s@,;]+@[^\s@,;]+$/.test((value || "").trim());

/**
 * Consulta a IUGU por um termo livre (e-mail, CPF/CNPJ ou nome).
 * A API devolve uma lista; normalizamos para sempre retornar array.
 *
 * Falha na consulta lança erro e NUNCA vira lista vazia: quem chama
 * entenderia "não existe", criaria uma conta nova e duplicaria o cadastro.
 * Antes de desistir, tenta de novo com espera crescente (limite da IUGU).
 */
export const searchIuguCustomers = async (
  query: string,
  options: IuguRetryOptions = {}
): Promise<IuguCustomer[]> => {
  const term = (query || "").trim();
  if (!term) return [];
  const delays = options.retryDelaysMs || SEARCH_RETRY_SHORT_MS;

  for (let attempt = 0; ; attempt++) {
    try {
      return await searchIuguOnce(term);
    } catch (error) {
      if (attempt >= delays.length) throw error;
      options.onRetryWait?.(delays[attempt]);
      await waitOrStop(delays[attempt], options.shouldStop);
    }
  }
};

const searchIuguOnce = async (term: string): Promise<IuguCustomer[]> => {
  await waitIuguSlot();
  const response = await fetch(
    `${process.env.REACT_APP_API_PATH}/iugu/customers/${encodeURIComponent(
      term
    )}`,
    { method: "GET", headers: authHeaders() }
  );
  const data = await readJson(response);

  if (data?.errors) {
    throw new IuguError(
      `a IUGU recusou a consulta: ${describeIuguErrors(data.errors)}`
    );
  }
  if (!response.ok || data === null) {
    throw new IuguError(`falha ao consultar a IUGU (HTTP ${response.status})`);
  }

  if (Array.isArray(data)) return data;
  // Defesa: se algum dia a API voltar a devolver objeto único, ainda funciona.
  if (data.id) return [data];
  throw new IuguError("resposta inesperada da IUGU na consulta");
};

const dedupeById = (customers: IuguCustomer[]) => {
  const seen = new Set<string>();
  return customers.filter((customer) => {
    if (!customer?.id || seen.has(customer.id)) return false;
    seen.add(customer.id);
    return true;
  });
};

/**
 * Procura conta existente na IUGU por e-mail e por documento.
 *
 * A busca da IUGU é aproximada (traz parecidos), então só consideramos
 * "já existe" quando e-mail ou CPF/CNPJ batem exatamente — caso contrário
 * devolvemos os candidatos para conferência manual, sem criar nada.
 */
export const findIuguCustomer = async (
  member: {
    email?: string | null;
    cpf?: string | null;
    cnpj?: string | null;
  },
  options: IuguRetryOptions = {}
): Promise<IuguMatch> => {
  // Campo de e-mail sujo (mais de um endereço etc.): busca só pelo documento
  const email = isSingleEmail(member.email)
    ? (member.email || "").trim().toLowerCase()
    : "";
  const document = onlyDigits(member.cnpj || member.cpf);

  let candidates: IuguCustomer[] = [];

  if (email) {
    const byEmail = await searchIuguCustomers(email, options);
    candidates = candidates.concat(byEmail);

    const exact = byEmail.find(
      (customer) => (customer.email || "").trim().toLowerCase() === email
    );
    if (exact) {
      return { customer: exact, matchedBy: "email", candidates: dedupeById(candidates) };
    }
  }

  // Muitos sócios foram cadastrados na IUGU antes, às vezes com outro
  // e-mail. O documento é o identificador confiável.
  if (document) {
    const byDocument = await searchIuguCustomers(document, options);
    candidates = candidates.concat(byDocument);

    const exact = byDocument.find(
      (customer) => onlyDigits(customer.cpf_cnpj) === document
    );
    if (exact) {
      return {
        customer: exact,
        matchedBy: "documento",
        candidates: dedupeById(candidates),
      };
    }
  }

  return { customer: null, matchedBy: null, candidates: dedupeById(candidates) };
};

/**
 * Cria o cliente na IUGU e devolve o registro criado.
 *
 * Quando a IUGU recusa o cadastro (e-mail inválido, CPF com dígito
 * errado...), a API do sistema responde 201 com `{ errors }` no corpo — a
 * biblioteca da IUGU no backend só reconhece `error`, no singular. Por isso
 * a recusa é detectada aqui, pelo conteúdo; antes ela passava em silêncio
 * e o botão "Criar & Vincular" parecia não fazer nada.
 */
export const createIuguCustomer = async (data: {
  email: string;
  name: string;
  cpf_cnpj: string;
}): Promise<IuguCustomer> => {
  await waitIuguSlot();
  const response = await fetch(
    `${process.env.REACT_APP_API_PATH}/iugu/customers`,
    {
      method: "POST",
      headers: authHeaders(),
      body: JSON.stringify({
        ...data,
        // Só o espaço nas pontas — o resto vai como está na ficha
        email: (data.email || "").trim(),
        name: (data.name || "").trim(),
      }),
    }
  );
  const body = await readJson(response);

  if (body?.errors) {
    throw new IuguError(
      `a IUGU recusou o cadastro: ${describeIuguErrors(body.errors)}`,
      true
    );
  }
  if (!response.ok) {
    throw new IuguError(
      `falha ao comunicar com a IUGU (HTTP ${response.status})`
    );
  }
  if (!body?.id) {
    throw new IuguError("a IUGU não retornou o cadastro");
  }
  return body;
};
